import { PlusCircleOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import {
  Button,
  Col,
  Drawer,
  Form,
  Input,
  Row,
  message,
  Divider,
  TreeSelect,
  Spin,
  Select,
  Switch,
  InputNumber,
  Typography,
} from "antd";
import React, { useState, useEffect } from "react";
import { store } from "../../../../redux/configStore";
import { transformData } from "../../../../utils/recursive";
import useStoreData from "../../../../hooks/useStoreData";
import {
  getListPointMechanisms,
  createPointMechanisms,
  updatePointMechanisms,
} from "../../../../api/pointMechanisms/pointMechanisms";
import { getListRewardType } from "../../../../api/rewardType/rewardType";
import { getListZaloEvent } from "../../../../api/zaloEvent/zaloEvent";
import { getListService } from "../../../../api/serviceProject/service";
import { brand } from "../../../../theme/tokens";

const { Text } = Typography;

const sectionTitleStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: brand.accent,
  margin: 0,
};

export default function FormAddPointMechanisms({
  isModalOpen,
  handleCancel,
  currentPage,
  pageSize,
  branchFilter,
  editRecord = null,
}) {
  const [isLoadCreate, setIsLoadCreate] = useState(false);
  const [formRef] = Form.useForm();
  const [isZaloMechanism, setIsZaloMechanism] = useState(false);
  const isEdit = !!editRecord;

  const { data: allListStore, loading: isLoadStoreAll } = useStoreData();

  const { listRewardTypes } = useSelector(
    (state) => state.persistedReducer.rewardTypeSlice
  );

  const { listZaloEvents } = useSelector(
    (state) => state.persistedReducer.zaloEventSlice
  );

  const { listService } = useSelector(
    (state) => state.persistedReducer.servicesSlice
  );

  // Chuyển đổi dữ liệu cho TreeSelect
  const data = transformData(allListStore?.data);
  const options = data?.filter((item) => item.parent_id === 0);

  useEffect(() => {
    const fetchData = async () => {
      const requests = [];

      // Check và gọi API cho danh sách loại cơ chế
      // if (!listRewardTypes || listRewardTypes.length === 0) {
        requests.push(store.dispatch(getListRewardType({ currentPage: 1, pageSize: 50 })));
      // }

      // Check và gọi API cho danh sách sự kiện Zalo
      // if (!listZaloEvents || listZaloEvents.length === 0) {
        requests.push(store.dispatch(getListZaloEvent({ currentPage: 1, pageSize: 50 })));
      // }

      // if (
      //   listService.length === 0
      // ) {
       await store.dispatch(getListService({ currentPage:1, pageSize:50 }));
      // }

      // Chờ tất cả các API hoàn thành
      await Promise.all(requests);
    };

    fetchData();
  // }, [listRewardTypes, allListStore, listZaloEvents, listService]);
  }, []);

  // Prefill khi mở ở chế độ sửa; reset khi mở để thêm mới.
  useEffect(() => {
    if (!isModalOpen) return;
    if (editRecord) {
      formRef.setFieldsValue({
        service_id: editRecord.service_id,
        reward_type_id: editRecord.reward_type_id ?? editRecord.reward_type?.id,
        zalo_event_id: editRecord.zalo_event_id ?? editRecord.zalo_event?.id,
        name: editRecord.name,
        description: editRecord.description,
        conversion_rate: editRecord.conversion_rate,
        value: editRecord.value,
        point_expiry_enabled:
          editRecord.point_expiry_enabled === 1 || editRecord.point_expiry_enabled === true,
        expires_in_days: editRecord.expires_in_days,
        mcoupon_branch_id: editRecord.mcoupon_branch_id,
      });
    } else {
      formRef.resetFields();
      setIsZaloMechanism(false);
    }
  }, [isModalOpen, editRecord, formRef]);

  // Xác định cơ chế zalo khi sửa. Tách riêng khỏi effect prefill vì listRewardTypes
  // có thể về sau khi mở form: nếu record không kèm object reward_type mà chỉ có id,
  // suy code theo danh sách -> tránh ẩn mất ô "Sự kiện" rồi submit thiếu zalo_event_id.
  useEffect(() => {
    if (!isModalOpen || !editRecord) return;
    const rewardTypeId = editRecord.reward_type_id ?? editRecord.reward_type?.id;
    const code =
      editRecord.reward_type?.code ??
      listRewardTypes?.find((type) => type.id === rewardTypeId)?.code;
    setIsZaloMechanism(code === "zalo");
  }, [isModalOpen, editRecord, listRewardTypes]);

  // Xử lý khi nhấn nút "Thêm"
  const onFinish = async (fieldsValue) => {
    const values = { ...fieldsValue };
    setIsLoadCreate(true);

    try {
      if (isEdit) {
        const response = await store.dispatch(
          updatePointMechanisms({ id: editRecord.id, data: values })
        );
        if (updatePointMechanisms.fulfilled.match(response)) {
          message.success("Cập nhật cơ chế thành công");
          handleCancel();
          await store.dispatch(
            getListPointMechanisms({ currentPage, pageSize, branchId: branchFilter })
          );
        } else {
          handleErrors(response);
        }
        return;
      }

      const response = await store.dispatch(createPointMechanisms(values));
      if (createPointMechanisms.fulfilled.match(response)) {
        if (response.payload.data.original) {
          message.error(response.payload.data.original.message);
        } else {
          message.success("Thêm cơ chế thành công");
          formRef.resetFields();
          handleCancel();
          await store.dispatch(
            getListPointMechanisms({ currentPage, pageSize, branchId: branchFilter })
          );
        }
      } else if (createPointMechanisms.rejected.match(response)) {
        handleErrors(response);
      }
    } catch (error) {
      message.error(isEdit ? "Đã xảy ra lỗi khi cập nhật cơ chế." : "Đã xảy ra lỗi khi tạo cơ chế.");
    } finally {
      setIsLoadCreate(false);
    }
  };

  // Hàm xử lý lỗi từ API
  const handleErrors = (response) => {
    if (response.payload.errors) {
      Object.keys(response.payload.errors).forEach((field) => {
        const errorMessages = response.payload.errors[field];
        errorMessages.forEach((errorMessage) => {
          message.error(errorMessage);
        });
      });
    } else if (response.payload.message) {
      message.error(response.payload.message);
    }
  };

  // Chỉ "Loại cơ chế" quyết định có phải cơ chế zalo hay không.
  const handleRewardTypeChange = (value) => {
    const selectedType = listRewardTypes?.find((type) => type.id === value);
    if (selectedType?.code === "zalo") {
      setIsZaloMechanism(true);
    } else {
      setIsZaloMechanism(false);

      // Xóa giá trị của trường liên quan khi không phải "zalo"
      formRef.setFieldsValue({
        zalo_event_id: null, // Reset giá trị trường "Sự kiện"
      });
    }
  };

  return (
    <Drawer
      title={
        <span style={{ fontSize: 18, fontWeight: 700 }}>
          {isEdit ? "Sửa cơ chế tích điểm" : "Thêm cơ chế tích điểm"}
        </span>
      }
      open={isModalOpen}
      onClose={handleCancel}
      width={640}
      destroyOnClose
      footer={
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "4px 0",
          }}
        >
          <Button onClick={handleCancel} style={{ minWidth: 80 }}>
            Hủy
          </Button>
          <Button
            type="primary"
            icon={isEdit ? null : <PlusCircleOutlined />}
            // Footer nằm ngoài <Form> nên htmlType="submit" không chạy -> submit bằng instance.
            onClick={() => formRef.submit()}
            loading={isLoadCreate}
            style={{ minWidth: 120 }}
          >
            {isEdit ? "Lưu thay đổi" : "Thêm cơ chế"}
          </Button>
        </div>
      }
    >
      <Form
        name="addPointMechanisms"
        form={formRef}
        onFinish={onFinish}
        layout="vertical"
        requiredMark="optional"
        style={{ marginTop: 8 }}
      >
        <Divider orientation="left" orientationMargin={0} style={{ marginTop: 0, marginBottom: 16 }}>
          <Text style={sectionTitleStyle}>Phân loại cơ chế</Text>
        </Divider>

        <Form.Item
          label="Loại dịch vụ"
          name="service_id"
          rules={[{ required: true, message: "Vui lòng chọn loại dịch vụ" }]}
        >
          <Select
            placeholder="Chọn loại dịch vụ"
            allowClear
            showSearch
            optionFilterProp="label"
            options={(listService || [])?.map((type) => ({
              label: type.name,
              value: type.id,
            }))}
          />
        </Form.Item>

        <Form.Item
          label="Loại cơ chế"
          name="reward_type_id"
          rules={[{ required: true, message: "Vui lòng chọn loại cơ chế" }]}
        >
          <Select
            placeholder="Chọn loại cơ chế"
            allowClear
            showSearch
            optionFilterProp="label"
            onChange={handleRewardTypeChange}
            options={(listRewardTypes || [])?.map((type) => ({
              label: type.name,
              value: type.id,
            }))}
          />
        </Form.Item>

        {/* Sự kiện (hiển thị khi cơ chế là zalo) */}
        {isZaloMechanism && (
          <Form.Item
            label="Sự kiện"
            name="zalo_event_id"
            rules={[{ required: true, message: "Vui lòng chọn loại sự kiện" }]}
          >
            <Select
              placeholder="Chọn loại sự kiện"
              allowClear
              showSearch
              optionFilterProp="label"
              options={(listZaloEvents || []).map((event) => ({
                label: `${event.name} [${event.code}]`,
                value: event.id,
              }))}
            />
          </Form.Item>
        )}

        <Divider orientation="left" orientationMargin={0} style={{ marginBottom: 16 }}>
          <Text style={sectionTitleStyle}>Thông tin cơ chế</Text>
        </Divider>

        <Row>
          <Col span={24}>
            <Form.Item
              label="Tên cơ chế"
              name="name"
              rules={[{ required: true, message: "Vui lòng nhập tên cơ chế" }]}
            >
              <Input placeholder="Nhập tên cơ chế" />
            </Form.Item>
          </Col>
        </Row>

        <Row>
          <Col span={24}>
            <Form.Item label="Mô tả cơ chế" name="description">
              <Input.TextArea
                rows={4}
                placeholder="Nhập mô tả cho cơ chế (nếu có)"
                autoSize={{ minRows: 3, maxRows: 5 }}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Tỷ lệ chuyển đổi và Giá trị tích lũy */}
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Tỷ lệ chuyển đổi"
              name="conversion_rate"
              rules={[{ required: true, message: "Vui lòng nhập tỷ lệ chuyển đổi" }]}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="Nhập tỷ lệ chuyển đổi"
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Giá trị tích lũy"
              name="value"
              rules={[{ required: true, message: "Vui lòng nhập giá trị tích lũy" }]}
            >
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder="Nhập giá trị tích lũy"
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" orientationMargin={0} style={{ marginBottom: 16 }}>
          <Text style={sectionTitleStyle}>Hạn dùng điểm</Text>
        </Divider>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              label="Kích hoạt hạn dùng điểm"
              name="point_expiry_enabled"
              valuePropName="checked"
              initialValue={false}
            >
              <Switch checkedChildren="Có hạn" unCheckedChildren="Vĩnh viễn" />
            </Form.Item>
          </Col>
          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) =>
              prevValues.point_expiry_enabled !== currentValues.point_expiry_enabled
            }
          >
            {({ getFieldValue }) =>
              getFieldValue("point_expiry_enabled") ? (
                <Col xs={24} sm={12}>
                  <Form.Item
                    label="Số ngày hết hạn"
                    name="expires_in_days"
                    rules={[{ required: true, message: "Vui lòng nhập số ngày hết hạn" }]}
                    initialValue={365}
                  >
                    <InputNumber min={1} style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
              ) : null
            }
          </Form.Item>
        </Row>

        <Divider orientation="left" orientationMargin={0} style={{ marginBottom: 16 }}>
          <Text style={sectionTitleStyle}>Đơn vị quản lý</Text>
        </Divider>

        <Form.Item
          label="Đơn vị"
          name="mcoupon_branch_id"
          rules={[{ required: true, message: "Vui lòng chọn đơn vị" }]}
        >
          {isLoadStoreAll ? (
            <Spin />
          ) : (
            <TreeSelect
              allowClear
              showSearch
              treeLine
              treeData={options}
              placeholder="Vui lòng chọn đơn vị"
              filterTreeNode={(inputValue, treeNode) =>
                treeNode.title.toLowerCase().includes(inputValue.toLowerCase())
              }
            />
          )}
        </Form.Item>
      </Form>
    </Drawer>
  );
}
